import React, { useState, useCallback, useRef } from 'react'
import { useStyles } from './styles'
import WebApp from '@twa-dev/sdk'

interface RinggyProps {
  isEnabled?: boolean
  onTap?: (tapCount: number) => void
}

export default function Ringgy(props: RinggyProps) {
  const { classes } = useStyles()
  const [rings, setRings] = useState<{ x: number; y: number; id: number }[]>([])
  const [tapCount, setTapCount] = useState(0)
  const isActiveTouchRef = useRef(false)

  const createRing = useCallback((x: number, y: number) => {
    const newRing = {
      x,
      y,
      id: Date.now() + Math.random(),
    }
    setRings((current) => [...current, newRing])
  }, [])

  const handleTap = useCallback(
    (x: number, y: number) => {
      const newTapCount = tapCount + 1
      setTapCount(newTapCount)

      createRing(x, y)

      props.onTap?.(newTapCount)
    },
    [tapCount, createRing, props]
  )

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      // Check if interactions are enabled
      if (!props.isEnabled) return
      WebApp.HapticFeedback.impactOccurred('rigid')
      event.preventDefault()
      event.stopPropagation()

      // Prevent multiple rings on iOS
      if (isActiveTouchRef.current) return

      isActiveTouchRef.current = true
      Array.from(event.touches).forEach((touch) => {
        handleTap(touch.clientX, touch.clientY)
      })
    },
    [handleTap, props.isEnabled]
  )

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!props.isEnabled) return

      event.preventDefault()
      event.stopPropagation()

      isActiveTouchRef.current = false
    },
    [props.isEnabled]
  )

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!props.isEnabled) return

      event.preventDefault()
      event.stopPropagation()

      handleTap(event.clientX, event.clientY)
    },
    [handleTap, props.isEnabled]
  )

  // If not enabled, don't render anything
  if (!props.isEnabled) return null

  return (
    <div
      className={classes.container}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      style={{
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      {rings.map((ring) => (
        <div
          key={ring.id}
          className={classes.ring}
          style={{
            left: `${ring.x}px`,
            top: `${ring.y}px`,
          }}
          onAnimationEnd={() => {
            setRings((current) => current.filter((r) => r.id !== ring.id))
          }}
        />
      ))}
    </div>
  )
}
